import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators, ɵInternalFormsSharedModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, finalize, forkJoin, map, of, Subscription, switchMap, tap } from 'rxjs';
import { Helper } from 'src/app/accountancy/utilities/classes/helper.class';
import { PromptMessage } from 'src/app/accountancy/utilities/classes/promp-message.class';
import { WaitHandler } from 'src/app/accountancy/utilities/classes/wait-handler.class';
import { ReimbursementInterface } from 'src/app/accountancy/utilities/interfaces/reimbursement.interface';
import { GlobalApiService } from 'src/app/accountancy/utilities/services/global-api/global-api.service';
import { ReimbursementService } from '../reimbursement-service/reimbursement.service';
import { AccConfirmationService } from 'src/app/accountancy/custom-components/acc-confirmation/acc-confirmation-service/acc-confirmation.service';

@Component({
  selector: 'app-reimbursement-form',
  templateUrl: './reimbursement-form.component.html',
  styleUrls: ['./reimbursement-form.component.scss'],
})
export class ReimbursementFormComponent implements OnInit {

  public readonly waitHandler = new WaitHandler();
  public readonly Helper = Helper;
  public readonly prompt = new PromptMessage();
  public readonly fg = this._newFg();
  public readonly totalComputedAmount$ = new BehaviorSubject(0.0);
  public readonly deletedItemIds: number[] = [];

  constructor(
    private _ref: MatDialogRef<ReimbursementFormComponent>,
    @Inject(MAT_DIALOG_DATA) private _data: ReimbursementInterface | undefined,

    private _confirm: AccConfirmationService,
    private _reimS: ReimbursementService,
    private _gApiS: GlobalApiService,
  ) { }

  public ngOnInit(): void {
    this.fg.controls.Purpose.patchValue(this._data?.Purpose, { emitEvent: false });

    this._data?.Items.forEach(item => this.addItem({
      Id: item.Id,
      VatableSales: item.VatableSales,
      VAT: item.VAT,
      Proof: new File([], item.Proof),
      Remarks: item.Remarks,
      Description: item.Description,
    }));
  }

  public onSubmit() {
    const formData = this.fg.value;

    if (Helper.getInvalidControls(this.fg, true).length) {
      this.prompt.set(Helper.errorMessage.requiredFields, 'danger');
      return;
    }

    if (!formData.Items?.length) {
      this.prompt.set("Please add at least 1 Item to continue.", 'danger');
      return;
    }

    const [year, month, date, day, h, m, s, ms] = Helper.date.dateDestruct(new Date());

    const subs: Subscription = of(formData).pipe(
      finalize(() => subs.unsubscribe()),

      // Get Presigned URL
      switchMap(data => {
        return of({ ...data, Items: data.Items?.map(item => ({ ...item })) }).pipe(
          switchMap(rawData => {
            const items = rawData.Items;
            const parsedItems = items?.map(item => of(item).pipe(
              tap(() => this.waitHandler.newRequest()),
              switchMap(item => {
                const file = item.Proof as File;
                const fileName = `${year}${month}${date}${day}${h}${m}${s}${ms}_${file.name.trim()}`

                if (file.size > 0) return this._gApiS.getUploadFileUrl(file, fileName, "Receipts").pipe(
                  finalize(() => this.waitHandler.finishRequest()),
                  map(presignedURL => ({ ...item, Proof: fileName, presignedURL: { file, url: presignedURL.body?.presignedURL } }))
                )

                else return of({ ...item, Proof: item.Proof?.name ?? '', presignedURL: { file, url: undefined } }).pipe(
                  finalize(() => this.waitHandler.finishRequest())
                );
              }),
            )) ?? [];

            return forkJoin(parsedItems).pipe(
              map(convertedItem => ({ ...rawData, Items: convertedItem }))
            );
          }),
        );
      }),

      // Put the FIle to server
      switchMap(data => {
        const items = data.Items;
        const parsedItems = items.map(item => of(null).pipe(
          tap(() => this.waitHandler.newRequest()),
          switchMap(() => {
            const url = item.presignedURL.url;
            const file = item.presignedURL.file;

            if (url && file) return this._gApiS.putFileInServer(url, file).pipe(
              finalize(() => this.waitHandler.finishRequest()),
              map(values => values.body)
            )

            else return of(null).pipe(
              finalize(() => this.waitHandler.finishRequest())
            )
          })
        ));

        return forkJoin(parsedItems).pipe(
          map(() => data),
        );
      }),

      // Path FIlename and call POST/PUT API
      switchMap(data => {
        const body = {
          ...data,
          Items: data.Items.map(item => ({ ...item, presignedURL: undefined })),
        };

        const controlNumber = this._data?.CntrlNumber;
        if (controlNumber) return of(controlNumber).pipe(

          // Update Reimbursement
          switchMap(cntrlNum => {
            return of(null).pipe(
              tap(() => this.waitHandler.newRequest()),
              switchMap(() => this._reimS.updateReimbursement(cntrlNum, { Purpose: body.Purpose }).pipe(
                finalize(() => this.waitHandler.finishRequest()),
                map(() => cntrlNum)
              ))
            );
          }),

          // Update/Add/Delete Items
          switchMap(cntrlNum => {
            const newItemsApi = of(null).pipe(
              tap(() => this.waitHandler.newRequest()),
              switchMap(() => this._reimS.newReimbursementItem(cntrlNum, { Items: body.Items.filter(item => !item.Id).map(item => ({ ...item, Id: undefined })) }).pipe(
                finalize(() => this.waitHandler.finishRequest()),
              ))
            );

            const updateItems = body.Items
              .filter(item => item.Id)
              .map(item => of(item).pipe(
                tap(() => this.waitHandler.newRequest()),
                switchMap(() => this._reimS.updateReimbursmentItem(cntrlNum, item.Id ?? -1, { ...item, Id: undefined }).pipe(
                  finalize(() => this.waitHandler.finishRequest())
                ))
              ));

            const deleteItemsApi = this.deletedItemIds
              .map(itemId => of(itemId).pipe(
                tap(() => this.waitHandler.newRequest()),
                switchMap(() => this._reimS.deleteReimbursmentItem(cntrlNum, itemId).pipe(
                  finalize(() => this.waitHandler.finishRequest())
                ))
              ));


            return forkJoin([newItemsApi, ...updateItems, ...deleteItemsApi]).pipe(
              finalize(() => {
                this._ref.close();
                subs.unsubscribe();
              })
            );



            // const newItems = body.Items
            //   .filter(item => !item.Id)
            //   .map(item => of(item).pipe(
            //     tap(() => this.waitHandler.newRequest()),
            //     switchMap(() => this._reimS.newReimbursementItem(cntrlNum, { ...item, Id: undefined }).pipe(
            //       finalize(() => this.waitHandler.finishRequest())
            //     ))
            //   ));

            // const updateItems = body.Items
            //   .filter(item => item.Id)
            //   .map(item => of(item).pipe(
            //     tap(() => this.waitHandler.newRequest()),
            //     switchMap(() => this._reimS.updateReimbursmentItem(cntrlNum, item.Id ?? -1, { ...item, Id: undefined }).pipe(
            //       finalize(() => this.waitHandler.finishRequest())
            //     ))
            //   ));

            // const deleteItems = this.deletedItemIds
            //   .map(itemId => of(itemId).pipe(
            //     tap(() => this.waitHandler.newRequest()),
            //     switchMap(() => this._reimS.deleteReimbursmentItem(cntrlNum, itemId).pipe(
            //       finalize(() => this.waitHandler.finishRequest())
            //     ))
            //   ));

            // return forkJoin([...newItems, ...updateItems, ...deleteItems]).pipe(
            //   finalize(() => {
            //     this._ref.close();
            //     subs.unsubscribe();
            //   })
            // );
          }),
        )

        else return of(null).pipe(
          tap(() => this.waitHandler.newRequest()),
          switchMap(() => this._reimS.newReimbursement(body).pipe(
            finalize(() => {
              this.waitHandler.finishRequest();
              this._ref.close();
              subs.unsubscribe();
            }),
          ))
        )
      }),
    ).subscribe();
  }

  public addItem(value?: ItemFormValueInterface) {
    this.fg.controls.Items.push(this._newFgItem(value));
  }

  public removeItemAt(index: number, itemId?: number | null) {
    this._confirm.open({
      title: "Delete Item",
      message: `Are you sure you want to delete Item #${index + 1}`,
      onAnswer: (yes, matDialogRef, data) => {
        if (yes) {
          this.fg.controls.Items.removeAt(index);
          if (itemId) this.deletedItemIds.push(itemId);
        };
        matDialogRef.close();
      },
    });
  }

  public onUploadProof(e: Event, formgGroupIndex: number) {
    const files = (e.target as HTMLInputElement).files as FileList;
    this.fg.controls.Items.at(formgGroupIndex).controls.Proof.patchValue(files[0]);
  }

  private _newFgItem(value?: ItemFormValueInterface) {
    const handleComputeTotalAmount = () => {
      const computedAmount = this.fg.controls.Items.controls.reduce((c, n) => {
        const amount = n.controls.VatableSales.value ?? 0;
        const vat = n.controls.VAT.value ?? 0;

        return c + (amount + vat);
      }, 0);
      this.totalComputedAmount$.next(computedAmount);
    };

    const fcVatableSales = Helper.reactiveForms.control<number>(value?.VatableSales, [Validators.required]);
    fcVatableSales.valueChanges.subscribe(handleComputeTotalAmount);

    const fcVAT = Helper.reactiveForms.control<number>(value?.VAT);
    fcVAT.valueChanges.subscribe(handleComputeTotalAmount);

    return new FormGroup<ItemFormInterface>({
      Id: Helper.reactiveForms.control<number>(value?.Id),
      VatableSales: fcVatableSales,
      VAT: fcVAT,
      Proof: Helper.reactiveForms.control<File>(value?.Proof, [Validators.required]),
      Remarks: Helper.reactiveForms.control<string>(value?.Remarks),
      Description: Helper.reactiveForms.control<string>(value?.Description, [Validators.required]),
    })
  }

  private _newFg(value?: ReimbursementInterface) {
    return new FormGroup({
      Purpose: Helper.reactiveForms.control<string>(value?.Purpose, [Validators.required]),
      Items: new FormArray<FormGroup<ItemFormInterface>>([]),
    });
  }

}

interface ItemFormInterface {
  Id: FormControl<number | null | undefined>;
  VatableSales: FormControl<number | null | undefined>;
  VAT: FormControl<number | null | undefined>;
  Proof: FormControl<File | null | undefined>;
  Remarks: FormControl<string | null | undefined>;
  Description: FormControl<string | null | undefined>;
}

interface ItemFormValueInterface {
  VatableSales: number | null | undefined;
  Id: number | null | undefined;
  VAT: number | null | undefined;
  Proof: File | null | undefined;
  Remarks: string | null | undefined;
  Description: string | null | undefined;
}